import { Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { getBackendErrors } from "api/errors";
import { useMember } from "api/members";
import { FormButtons } from "components/FormControls/FormButtons";
import { NumberFormatInput } from "components/FormControls/NumberFormatInput";
import { ReactComponent as WarningTriangleIcon } from "components/Icons/warning-triangle.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik, FormikHelpers } from "formik";
import { useMessage } from "hooks/intl";
import { backendErrors } from "utilities/backendErrors";
import { EditedBadge } from "./Badges";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    description: {
      fontSize: 14,
      color: theme.gray800,
      marginBottom: theme.spacing(8),
    },
    warnContainer: {
      backgroundColor: theme.orange50,
      borderRadius: "4px",
      padding: theme.spacing(8),
      margin: theme.spacing(5, 0, 24),
      fontSize: 14,
      display: "flex",
      gap: theme.spacing(4),

      "& svg": {
        color: theme.orange700,
        width: 24,
        minWidth: 24,
      },
    },
    currentValue: {
      marginBottom: theme.spacing(12),
    },
    value: {
      fontWeight: 600,
      color: theme.gray800,
      marginLeft: theme.spacing(2),
    },
    btnWrapper: {
      justifyContent: "space-between",
      marginBottom: 0,

      "& button": {
        width: "100%",
      },
    },
  }),
);

interface Props {
  badge: EditedBadge;
  onClose: () => void;
  memberId: string;
}

type FormValues = {
  completedCount: number;
};

export const EditCompletionCountModal = ({
  badge,
  memberId,
  onClose,
}: Props) => {
  const classes = useStyles();
  const { notify } = useNotifications();
  const { message } = useMessage();
  const { updateMemberBadgeCompletionCount } = useMember(memberId);

  const onSubmit = async (
    values: FormValues,
    { setErrors }: FormikHelpers<FormValues>,
  ) => {
    return updateMemberBadgeCompletionCount({
      badgeId: badge.badgeId as string,
      completedCount: values.completedCount,
    })
      .then(() => {
        notify(message({ id: "badgeCompletedCountUpdated" }));
        onClose();
      })
      .catch((exception) => {
        const errors = backendErrors(getBackendErrors(exception));
        setErrors(errors);
      });
  };

  return (
    <Formik
      initialValues={{ completedCount: badge.completedCount || 0 }}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, isSubmitting, errors }) => {
        return (
          <Form onSubmit={handleSubmit}>
            <Typography className={classes.description}>
              {message({ id: "editCompletionCountDesc" })}
            </Typography>
            <Typography className={classes.currentValue}>
              {message({ id: "currentValue" })}:
              <span className={classes.value}>{badge.completedCount || 0}</span>
            </Typography>
            <NumberFormatInput
              name="completedCount"
              label={message({ id: "badgeCount" })}
              width="100%"
              allowNegative={false}
            />
            <div className={classes.warnContainer}>
              <WarningTriangleIcon />
              <Typography fontSize={14}>
                {message({ id: "editCompletionCountWarn" })}
              </Typography>
            </div>
            <FormButtons
              className={classes.btnWrapper}
              onCancelClick={onClose}
              onSubmitClick={() => handleSubmit()}
              submitMessage={message({ id: "saveChanges" })}
              cancelMessage={message({ id: "cancel" })}
              disableSubmit={isSubmitting}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
